import React from "react";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { AreaClosed, LinePath } from "@visx/shape";
import { TooltipWithBounds, useTooltip } from "@visx/tooltip";
import { AXIS_LABEL, TOOLTIP_STYLE } from "./chartTheme";
import useArrowFocus from "./useArrowFocus";
import useHideOnEscape from "./useHideOnEscape";

// A evolucao dia a dia (spec do redesign, R4.4): a linha do periodo com uma area clara por baixo e, a
// tracejado, a do periodo anterior, na mesma escala (um eixo so: e a mesma metrica). Cada dia e uma
// marca focavel, como as barras do kit: as setas andam entre os dias e a tooltip diz os dois periodos.
// O maximo fica marcado, com a etiqueta do lado em que cabe e um halo da cor do cartao (o contorno pinta-se
// por baixo do texto), para a linha tracejada do anterior nao lhe passar por cima; um ultimo dia ainda a
// decorrer fica a tracejado. Um dia sem valor (`value` a null, como a latencia de um dia sem respostas)
// parte a linha, a do periodo e a do anterior, em vez de a levar a zero ou de ligar os vizinhos; o ponto que
// fica sozinho entre dois dias sem valor desenha-se como um pontinho, porque uma linha de um ponto nao se ve
// (o do anterior vazado, como a sua linha tracejada: a cor sozinha nao chega para o distinguir).
const MARGIN = { top: 20, right: 12, bottom: 28, left: 48 };
// A etiqueta do maximo abre a direita do ponto e passa para a esquerda quando assim saisse da area do
// grafico (a 375 px fazia a pagina rolar de lado). A largura estima-se a 6,5 px por caracter, um pouco
// acima da media da Inter a 11 px: medir o texto pedia um segundo desenho, e uma etiqueta que muda de lado
// um pouco cedo nao custa nada.
const PEAK_GAP = 8;
const LABEL_CHAR_WIDTH = 6.5;
const ISOLATED_RADIUS = 2.5;

const LineChart = ({ points, width, height = 240, label, previousLabel, formatValue, formatTick, tickEvery = 1, partialLast = false, peakLabel }) => {
  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = height - MARGIN.top - MARGIN.bottom;
  const x = scaleBand({ domain: points.map((point) => point.key), range: [0, innerWidth], padding: 0 });
  const values = points.flatMap((point) => [point.value, point.previous]).filter((value) => value !== null);
  const y = scaleLinear({ domain: [0, Math.max(1, ...values)], range: [innerHeight, 0], nice: true });
  // Contagens e milissegundos: so graduacoes inteiras.
  const ticks = y.ticks(4).filter(Number.isInteger);
  const cx = (point) => x(point.key) + x.bandwidth() / 2;
  const labels = Object.fromEntries(points.map((point) => [point.key, point.label]));
  // Um ponto sem vizinho com valor e um segmento de um so ponto, que a linha nao desenha: leva um pontinho.
  const isolated = (field) => points.filter((point, index) => point[field] !== null && points[index - 1]?.[field] == null && points[index + 1]?.[field] == null);
  const solid = partialLast ? points.slice(0, -1) : points;
  const peak = points.reduce((best, point) => (point.value > best.value ? point : best), points[0]);
  const peakText = peakLabel ? peakLabel(peak) : "";
  const peakNearEnd = cx(peak) + PEAK_GAP + peakText.length * LABEL_CHAR_WIDTH > innerWidth;
  const { tooltipOpen, tooltipData, tooltipLeft, tooltipTop, showTooltip, hideTooltip } = useTooltip();
  const { active, setActive, refFor, onKeyDown } = useArrowFocus(points.length);
  useHideOnEscape(tooltipOpen, hideTooltip);

  const show = (point, index) => {
    setActive(index);
    showTooltip({ tooltipData: point, tooltipLeft: MARGIN.left + cx(point), tooltipTop: MARGIN.top + y(point.value ?? point.previous ?? 0) });
  };
  // O nome de um dia: "14 Oct: 9", e com o anterior "14 Oct: 9, Periodo anterior: 4"; um dia sem valor (um dia
  // por chegar, D8 do item 12, ou a latencia de um dia sem respostas) so tem o anterior, como nas barras.
  const nameOf = (point) => {
    const own = point.value === null ? point.label : `${point.label}: ${formatValue(point.value)}`;
    return point.previous === null ? own : `${own}, ${previousLabel}: ${formatValue(point.previous)}`;
  };

  return (
    <div className="relative">
      <svg width={width} height={height} role="group" aria-label={label} style={{ overflow: "visible" }}>
        <Group left={MARGIN.left} top={MARGIN.top}>
          <g aria-hidden="true">
            <GridRows scale={y} width={innerWidth} tickValues={ticks} stroke="var(--chart-grid)" />
            <AreaClosed
              data={points}
              x={cx}
              y={(point) => y(point.value)}
              yScale={y}
              defined={(point) => point.value !== null}
              fill="var(--chart-1)"
              fillOpacity={0.08}
            />
            <LinePath
              data={points}
              x={cx}
              y={(point) => y(point.previous)}
              defined={(point) => point.previous !== null}
              stroke="var(--chart-muted)"
              strokeWidth={1.5}
              strokeDasharray="5 4"
            />
            {isolated("previous").map((point) => (
              <circle key={point.key} data-isolated="previous" cx={cx(point)} cy={y(point.previous)} r={ISOLATED_RADIUS} fill="var(--color-surface)" stroke="var(--chart-muted)" strokeWidth={1.5} />
            ))}
            {isolated("value").map((point) => (
              <circle key={point.key} data-isolated="value" cx={cx(point)} cy={y(point.value)} r={ISOLATED_RADIUS} fill="var(--chart-1)" />
            ))}
            <LinePath data={solid} x={cx} y={(point) => y(point.value)} defined={(point) => point.value !== null} stroke="var(--chart-1)" strokeWidth={2} />
            {partialLast && points.length > 1 && (
              <g data-testid="partial-day">
                <LinePath
                  data={points.slice(-2)}
                  x={cx}
                  y={(point) => y(point.value)}
                  defined={(point) => point.value !== null}
                  stroke="var(--chart-1)"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                />
              </g>
            )}
            {peakLabel && peak.value > 0 && (
              <g>
                <circle cx={cx(peak)} cy={y(peak.value)} r={4} fill="var(--color-surface)" stroke="var(--chart-1)" strokeWidth={2} />
                <text
                  x={cx(peak) + (peakNearEnd ? -PEAK_GAP : PEAK_GAP)}
                  y={y(peak.value) - 8}
                  textAnchor={peakNearEnd ? "end" : "start"}
                  style={AXIS_LABEL}
                  stroke="var(--color-surface)"
                  strokeWidth={3}
                  strokeLinejoin="round"
                  paintOrder="stroke"
                >
                  {peakText}
                </text>
              </g>
            )}
          </g>
          {points.map((point, index) => (
            <g
              key={point.key}
              ref={refFor(index)}
              data-chart-mark=""
              tabIndex={index === active ? 0 : -1}
              role="img"
              aria-label={nameOf(point)}
              onFocus={() => show(point, index)}
              onBlur={hideTooltip}
              onMouseEnter={() => show(point, index)}
              onMouseLeave={hideTooltip}
              onKeyDown={(event) => onKeyDown(index, event)}
            >
              <rect x={x(point.key)} y={0} width={x.bandwidth()} height={innerHeight} fill="transparent" />
              {tooltipOpen && tooltipData?.key === point.key && point.value !== null && <circle cx={cx(point)} cy={y(point.value)} r={4} fill="var(--chart-1)" />}
            </g>
          ))}
          <g aria-hidden="true">
            <AxisLeft
              scale={y}
              tickValues={ticks}
              tickFormat={formatTick}
              stroke="var(--chart-grid)"
              tickStroke="var(--chart-grid)"
              tickLabelProps={{ ...AXIS_LABEL, textAnchor: "end", dx: -4, dy: 3 }}
            />
            <AxisBottom
              top={innerHeight}
              scale={x}
              tickValues={points.filter((_, index) => index % tickEvery === 0).map((point) => point.key)}
              tickFormat={(key) => labels[key]}
              stroke="var(--chart-grid)"
              tickStroke="var(--chart-grid)"
              tickLabelProps={{ ...AXIS_LABEL, textAnchor: "middle" }}
            />
          </g>
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds key={tooltipData.key} left={tooltipLeft} top={tooltipTop} applyPositionStyle style={TOOLTIP_STYLE}>
          <strong>{tooltipData.label}</strong>
          {tooltipData.value !== null && <>: {formatValue(tooltipData.value)}</>}
          {tooltipData.previous !== null && (
            <>
              <br />
              {previousLabel}: {formatValue(tooltipData.previous)}
            </>
          )}
        </TooltipWithBounds>
      )}
    </div>
  );
};

export default LineChart;
